import * as Sentry from "@sentry/react";
import axios from "axios";
import moment from "moment";
import toast from "react-hot-toast";
import {
  MAX_INTERACTION_FILE_SIZE_IN_MB,
  MAX_NARRATIVE_FILE_SIZE_IN_MB,
} from "../components/common/maxFileSize";

export type AudioUploadOptions = {
  speakers: { name: string; role: string }[];
  includeTimestamps: boolean;
  additionalContext: string;
  detailLevel: "detailed" | "standard" | "concise";
  // Upload method ("recording" | "upload") — lets callers pass options AND a
  // method to processAudioFile, which otherwise accepts one or the other
  method?: string;
  // Per-report call sign override (stored on the case; null = use preference)
  callSign?: string | null;
};

export type AudioFileInput = File | Blob | React.ChangeEvent<HTMLInputElement>;

export type AudioProcessingCallbacks = {
  onSuccess?: (data: any) => void;
  onError?: (error: string) => void;
  onProgress?: (progress: number) => void;
};

/**
 * Validates audio file size and type
 */
export const validateAudioFile = (
  file: File | Blob,
  audioType: string,
): boolean => {
  const MAX_FILE_SIZE =
    audioType === "narrative"
      ? MAX_NARRATIVE_FILE_SIZE_IN_MB * 1024 * 1024
      : MAX_INTERACTION_FILE_SIZE_IN_MB * 1024 * 1024;

  // Validate file size
  if (file.size > MAX_FILE_SIZE) {
    const maxSizeMB =
      audioType === "narrative"
        ? MAX_NARRATIVE_FILE_SIZE_IN_MB
        : MAX_INTERACTION_FILE_SIZE_IN_MB;
    toast.error(`File size must be less than ${maxSizeMB}MB`);
    return false;
  }

  const validTypes = ["audio/mpeg", "audio/x-m4a", "audio/wav"];
  if (audioType === "interaction") {
    validTypes.push("video/mp4");
  }

  if (!validTypes.includes(file.type)) {
    console.error(
      `Invalid audio file type: "${file.type}" for file "${file instanceof File ? file.name : "blob"}"`,
    );
    toast.error(
      `Please upload a valid audio file (MP3${
        audioType === "interaction" ? ", MP4" : ""
      }, M4A, WAV)`,
    );
    return false;
  }

  return true;
};

/**
 * Extracts file from various input formats
 */
export const extractFileFromInput = (
  fileOrEvent: AudioFileInput,
): File | Blob | null => {
  if (fileOrEvent instanceof File || fileOrEvent instanceof Blob) {
    return fileOrEvent;
  } else if (fileOrEvent.target && fileOrEvent.target.files) {
    return fileOrEvent.target.files[0];
  } else {
    console.error("Invalid input: expected File, Blob, or file input event");
    return null;
  }
};

/**
 * Creates audio metadata object for case creation/updates
 */
export const createAudioMetadata = (
  file: File | Blob,
  method: string,
  audioType: string,
  uploadOptions?: AudioUploadOptions,
  supplementalAudio?: File | null,
) => {
  return {
    source: "web",
    method,
    audioType,
    fileType: file.type,
    fileSize: `${(file.size / 1000000).toFixed(2)}MB`,
    ...(uploadOptions && {
      uploadOptions: {
        detailLevel: uploadOptions.detailLevel,
        includeTimestamps: uploadOptions.includeTimestamps,
        additionalContext: uploadOptions.additionalContext,
        ...(uploadOptions.speakers?.length > 0 && {
          speakers: uploadOptions.speakers,
        }),
      },
    }),
    ...(supplementalAudio && {
      supplementalAudio: {
        type: "narrative",
        fileSize: `${(supplementalAudio.size / 1000000).toFixed(2)}MB`,
        fileType: supplementalAudio.type,
        audioUrl: "",
        transcriptionUrl: "",
      },
    }),
  };
};

/**
 * Extracts file extension from a File or uses default for Blob
 */
export const getFileExtension = (file: File | Blob): string => {
  if (file instanceof File) {
    const extension = file.name.split(".").pop()?.toLowerCase();
    return extension || "m4a";
  }

  // For Blob, try to determine from MIME type
  const mimeTypeMap: Record<string, string> = {
    "audio/mpeg": "mp3",
    "audio/mp3": "mp3",
    "audio/x-m4a": "m4a",
    "audio/m4a": "m4a",
    "audio/mp4": "m4a",
    "audio/wav": "wav",
    "audio/wave": "wav",
    "audio/x-wav": "wav",
    "audio/aac": "aac",
    "audio/flac": "flac",
    "audio/ogg": "ogg",
    "audio/webm": "webm",
    "video/mp4": "mp4",
  };

  return mimeTypeMap[file.type] || "m4a";
};

/**
 * Creates URL request data for S3 upload
 */
export const createUrlRequestData = (
  user: any,
  file: File | Blob,
  audioType: string,
  caseId?: string,
  uploadOptions?: AudioUploadOptions,
) => {
  const urlRequestData: any = {
    userId: user?.id,
    fileName:
      file instanceof File
        ? file.name
        : `recorded_audio_${user?.id}_${moment().format("DD-MM-YYYY")}.${getFileExtension(file)}`,
    audioType,
  };

  // Add case-related metadata for interaction audio
  if (audioType === "interaction" && caseId) {
    urlRequestData.caseId = caseId;
    urlRequestData.userName = user?.name;
    urlRequestData.badgeId = user?.badgeID;
    urlRequestData.localDateTimeOffset = new Date()
      .getTimezoneOffset()
      .toString();

    if (uploadOptions) {
      urlRequestData.uploadOptions = {
        detailLevel: uploadOptions.detailLevel,
        includeTimestamps: uploadOptions.includeTimestamps,
        additionalContext: uploadOptions.additionalContext,
        ...(uploadOptions.speakers?.length > 0 && {
          speakers: uploadOptions.speakers,
        }),
      };
    }
  }

  return urlRequestData;
};

/**
 * Uploads audio file to S3
 */
export const uploadAudioToS3 = async (
  file: File | Blob,
  uploadURL: string,
  onProgress?: (progress: number, remainingSeconds?: number) => void,
  signal?: AbortSignal,
): Promise<void> => {
  const audioBuffer = await file.arrayBuffer();
  const audioBlob = new Blob([audioBuffer], { type: file.type });

  // Time tracking for remaining time calculation
  let startTime = Date.now();
  let lastTime = startTime;
  let lastLoaded = 0;
  let smoothSpeed = 0; // Exponential moving average of upload speed (bytes/sec)
  let lastUpdateTime = startTime;
  let lastRemainingSeconds: number | undefined; // Track last calculated value

  await axios.put(uploadURL, audioBlob, {
    headers: { "Content-Type": file.type },
    signal,
    ...(onProgress && {
      onUploadProgress: (progressEvent) => {
        const now = Date.now();
        const loaded = progressEvent.loaded;
        const total = progressEvent.total || audioBlob.size;
        const percentCompleted = Math.round((loaded * 100) / total);

        // Only update calculations every 500ms to avoid jitter
        if (now - lastUpdateTime >= 500) {
          const elapsed = (now - lastTime) / 1000; // Convert to seconds
          const bytesUploaded = loaded - lastLoaded;

          if (elapsed > 0 && bytesUploaded > 0) {
            const currentSpeed = bytesUploaded / elapsed;

            // Exponential Moving Average with alpha = 0.2
            // Balances smoothness with responsiveness
            smoothSpeed = smoothSpeed
              ? 0.2 * currentSpeed + 0.8 * smoothSpeed
              : currentSpeed;

            // Calculate remaining time only if we have reliable speed data
            // and have uploaded at least 5% (to avoid wild initial estimates)
            let remainingSeconds: number | undefined;
            if (smoothSpeed > 0 && percentCompleted >= 5) {
              const remaining = total - loaded;
              remainingSeconds = Math.ceil(remaining / smoothSpeed);

              // Cap max time at 2 hours to handle very slow speeds
              if (remainingSeconds > 7200) {
                remainingSeconds = undefined; // Show as calculating
              }
            }

            // Update last calculated value
            lastRemainingSeconds = remainingSeconds;
            onProgress(percentCompleted, remainingSeconds);
          } else {
            // If stalled or no data, keep showing last calculated time
            onProgress(percentCompleted, lastRemainingSeconds);
          }

          lastTime = now;
          lastLoaded = loaded;
          lastUpdateTime = now;
        } else if (
          percentCompleted !== Math.round((lastLoaded * 100) / total)
        ) {
          // Progress changed but update interval hasn't elapsed
          // Keep showing last calculated time
          onProgress(percentCompleted, lastRemainingSeconds);
        }
      },
    }),
  });
};

/**
 * Handles supplemental audio upload for interaction audio
 */
export const uploadSupplementalAudio = async (
  supplementalAudio: File,
  user: any,
  caseId: string,
  uploadOptions?: AudioUploadOptions,
  onProgress?: (progress: number, remainingSeconds?: number) => void,
  signal?: AbortSignal,
): Promise<string | null> => {
  try {
    // Get upload URL for supplemental audio
    const supplementalUrlResponse = await axios.post("/api/get-upload-url", {
      userId: user?.id,
      fileName: `supplemental_${supplementalAudio.name}`,
      audioType: "narrative",
      caseId: caseId,
      isSupplemental: true,
    });

    const { uploadURL: supplementalUploadURL, key: supplementalKey } =
      supplementalUrlResponse.data;

    // Upload supplemental audio
    await uploadAudioToS3(
      supplementalAudio,
      supplementalUploadURL,
      onProgress,
      signal,
    );

    // Update case with supplemental audio URL
    await axios.patch(`/api/cases/${caseId}`, {
      audioMetadata: {
        source: "web",
        method: "upload",
        audioType: "narrative",
        fileType: supplementalAudio.type,
        fileSize: `${(supplementalAudio.size / 1000000).toFixed(2)}MB`,
        supplementalAudio: {
          type: "narrative",
          audioUrl: `https://police-narratives-ai-audio.s3.${
            process.env.NODE_ENV === "production"
              ? "us-gov-west-1"
              : "us-west-2"
          }.amazonaws.com/${supplementalKey}`,
          fileSize: `${(supplementalAudio.size / 1000000).toFixed(2)}MB`,
          fileType: supplementalAudio.type,
          transcriptionUrl: "",
        },
      },
    });

    // Start processing supplemental audio
    await axios.post("/api/transcribe", {
      key: supplementalKey,
      caseId: caseId,
      localDateTimeOffset: new Date()
        .toLocaleString()
        .replace(/[^a-zA-Z0-9\s+\-=._:/@]/g, ""),
      audioType: "supplemental-narrative",
      uploadOptions: uploadOptions && {
        detailLevel: uploadOptions.detailLevel,
        additionalContext: uploadOptions.additionalContext,
      },
    });

    return supplementalKey;
  } catch (supplementalError) {
    console.error("Error processing supplemental audio:", supplementalError);
    // Re-throw error to let caller handle it
    throw supplementalError;
  }
};

/**
 * Generates S3 audio URL
 */
export const generateS3AudioUrl = (
  key?: string,
  audioType: string = "narrative",
): string => {
  if (key) {
    const bucket =
      audioType === "interaction"
        ? "police-narratives-ai-interaction-audio"
        : "police-narratives-ai-audio";

    return `https://${bucket}.s3.${
      process.env.NODE_ENV === "production" ? "us-gov-west-1" : "us-west-2"
    }.amazonaws.com/${key}`;
  } else {
    throw new Error("Invalid S3 key provided");
  }
};

/**
 * Handles audio download with error handling
 */
export const downloadAudioFile = (
  file: File | Blob,
  user: any,
  filename?: string,
) => {
  const fileExtension = getFileExtension(file);
  const url = URL.createObjectURL(file);
  const a = document.createElement("a");
  a.href = url;
  a.download =
    filename ||
    (file instanceof File
      ? file.name
      : `recorded_audio_${user?.id}_${moment().format("DD-MM-YYYY")}.${fileExtension}`);
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

/**
 * Shows error toast with download option - returns error info for custom toast creation
 */
export const getAudioErrorInfo = (error: any) => {
  let errorMessage = "There was an error processing your audio file.";

  if (axios.isAxiosError(error)) {
    if (error.response?.status === 401) {
      errorMessage = "Your session has expired. Please log in again.";
    } else if (error.response?.data?.error) {
      // Use the specific error message from the server
      errorMessage = error.response.data.error;
    }
  }

  return {
    errorMessage,
    description: "Please download your audio file and try again",
    actionLabel: "Download audio file",
  };
};

/**
 * Logs audio processing error to Sentry
 */
export const logAudioError = (
  error: any,
  component: string,
  action: string,
  file: File | Blob,
  extraData?: Record<string, any>,
) => {
  Sentry.captureException(error, {
    tags: {
      component,
      action,
    },
    extra: {
      fileType: file.type,
      fileSize: file.size,
      ...extraData,
    },
  });
};

/**
 * Polling function for processing status - moved from Menu component
 */
export const pollProcessingStatus = async (
  caseId: string,
  {
    onSuccess,
    onError,
    interval = 2000,
    timeout = 30 * 60 * 1000, // 30 minutes
    toastId,
    successMessage,
    cleanupToast,
  }: {
    onSuccess: (data: any) => void;
    onError?: (error: string) => void;
    interval?: number;
    timeout?: number;
    toastId?: string;
    successMessage?: string;
    cleanupToast?: (caseId: string) => void;
  },
) => {
  const endTime = Date.now() + timeout;

  const poll = setInterval(async () => {
    try {
      const response = await axios.get(
        `/api/cases/${caseId}/processing-status`,
      );

      if (response.data.status === "active") {
        clearInterval(poll);
        // Fetch complete case data
        const caseResponse = await axios.get(`/api/cases/${caseId}`);
        // Play completion sound
        try {
          const audio = new Audio("/finishsfx.wav");
          audio.volume = 0.5;
          audio.play().catch(() => {}); // Ignore autoplay restrictions
        } catch {}
        // Only show toast if successMessage is provided
        if (successMessage) {
          toast.success(successMessage, { duration: 5000, id: toastId });
        }
        // Clean up toast registry
        cleanupToast?.(caseId);
        onSuccess(caseResponse.data);
      } else if (response.data.status === "error") {
        clearInterval(poll);
        const errorMsg = "Error processing audio: " + response.data.error;
        // Only show toast if successMessage is provided (same pattern as success toast)
        if (successMessage) {
          toast.error(errorMsg, { duration: 5000, id: toastId });
        }
        // Clean up toast registry
        cleanupToast?.(caseId);
        onError?.(errorMsg);
      }

      // Stop polling if timeout reached
      if (Date.now() > endTime) {
        clearInterval(poll);
        const timeoutMsg = "Processing timed out";
        // Only show toast if successMessage is provided (same pattern as success toast)
        if (successMessage) {
          toast.error(timeoutMsg, { id: toastId });
        }
        // Clean up toast registry
        cleanupToast?.(caseId);
        onError?.(timeoutMsg);
      }
    } catch (error) {
      console.error("Error polling status:", error);
      logAudioError(error, "AudioUtils", "pollProcessingStatus", new Blob(), {
        caseId,
      });
      clearInterval(poll);
      // Clean up toast registry
      cleanupToast?.(caseId);
      onError?.("Error checking processing status");
    }
  }, interval);

  // Return cleanup function
  return () => clearInterval(poll);
};
